import { css } from '@emotion/react'
import { rem } from '@groovehq/internal-design-system/lib/util/strings'

const control = theme => css`
  min-height: ${rem(44)};
  white-space: normal;
  border-radius: ${rem(999)};
  padding: 0 ${rem(22)};
  font-size: ${theme.fontSize.xlarge};
  font-weight: ${theme.fontWeight.semibold};
  &:focus-visible,
  &:focus-within {
    outline: ${rem(2)} solid ${theme.color.info['500']};
    outline-offset: ${rem(3)};
  }
`
// Drawer forwards css/className to its panel; keep this surface white.
const drawer = theme => css`
  max-width: ${rem(520)};
  > div {
    background: ${theme.color.monochrome.white};
  }
  > div:first-of-type {
    background: ${theme.color.monochrome.white};
    color: ${theme.color.monochrome.black};
    border-bottom: 1px solid ${theme.color.monochrome.medium};
    padding: ${rem(18)} ${rem(24)};
    > h4 {
      font-size: ${theme.fontSize.x3large};
      font-weight: ${theme.fontWeight.semibold};
    }
    > button {
      box-sizing: border-box;
      width: ${rem(44)};
      height: ${rem(44)};
      padding: ${rem(14)};
      background: transparent;
      color: ${theme.color.monochrome.superDark};
      &:hover {
        background: ${theme.color.monochrome.light};
      }
    }
  }
`
const actions = theme => css`
  padding: ${rem(16)} ${rem(24)};
  border-top: 1px solid ${theme.color.monochrome.medium};
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${rem(10)};
`
const quietButton = theme => css`
  && {
    border-color: transparent;
    background: transparent;
    color: ${theme.color.monochrome.black};
  }
`
const primaryButton = css`
  border-radius: ${rem(999)};
`
const row = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${rem(12)};
  padding: ${rem(5)} 0;
`
const body = theme => css`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  padding-top: ${rem(22)};
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: ${theme.fontSize.large};
  line-height: 1.25;
  color: ${theme.color.monochrome.black};
  && strong {
    font-weight: ${theme.fontWeight.bold};
  }
`
const summary = theme => css`
  padding: ${rem(18)} ${rem(20)};
  background: ${theme.color.info['50']};
  border-radius: ${rem(10)};
  font-size: ${theme.fontSize.base};
  color: ${theme.color.monochrome.superDark};
  > strong {
    display: block;
    margin: ${rem(2)} 0;
    font-size: ${rem(30)};
    font-weight: ${theme.fontWeight.bold};
    color: ${theme.color.monochrome.black};
  }
  > p {
    margin: 0;
  }
`
const choices = theme => css`
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
  legend {
    padding: 0;
    margin-bottom: ${rem(10)};
    font-weight: ${theme.fontWeight.semibold};
  }
`
const option = theme => css`
  display: block;
  border: 1px solid ${theme.color.monochrome.medium};
  border-radius: ${rem(10)};
  label {
    box-sizing: border-box;
    width: 100%;
    padding: ${rem(16)};
    font-size: ${theme.fontSize.large};
    line-height: 1.25;
    > .grui {
      flex: 1;
      min-width: 0;
      margin-left: ${rem(14)};
    }
  }
  label .radio-btn {
    width: ${rem(18)};
    height: ${rem(18)};
    color: ${theme.color.info['500']};
  }
`
const selectedOption = theme => css`
  border: 2px solid ${theme.color.info['500']};
  background: ${theme.color.info['50']};
`
const optionHeading = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: ${rem(8)};
`
const chip = theme => css`
  border-radius: ${rem(999)};
  padding: ${rem(2)} ${rem(8)};
  font-size: ${theme.fontSize.small};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.color.positive['900']};
  background: ${theme.color.positive['50']};
`
const description = theme => css`
  display: block;
  margin-top: ${rem(4)};
  color: ${theme.color.monochrome.superDark};
`
const rows = theme => css`
  border-top: 1px solid ${theme.color.monochrome.medium};
  padding-top: ${rem(14)};
`
const muted = theme =>
  css`
    color: ${theme.color.monochrome.dark};
  `
const equivalent = theme => css`
  color: ${theme.color.monochrome.dark};
  font-size: ${theme.fontSize.base};
`
const dueToday = theme => css`
  font-size: ${theme.fontSize.x2large};
  font-weight: ${theme.fontWeight.bold};
  border-top: 1px solid ${theme.color.monochrome.medium};
  margin-top: ${rem(8)};
  padding-top: ${rem(12)};
`
const notes = theme => css`
  margin: 0;
  padding-left: ${rem(18)};
  font-size: ${theme.fontSize.small};
  line-height: 1.6;
  color: ${theme.color.monochrome.dark};
`
export const styles = {
  drawer,
  control,
  actions,
  quietButton,
  primaryButton,
  row,
  body,
  summary,
  choices,
  option,
  selectedOption,
  optionHeading,
  chip,
  description,
  rows,
  muted,
  equivalent,
  dueToday,
  notes,
}
